/**
 * Simple Library API Module
 * Handles basic library-related API operations
 */

import { api } from "../core/api.client";
import {
  ApiClientResponse,
  QueryParams,
  RequestOptions,
} from "../core/api.types";

// Import the LibraryArticle type
import { LibraryArticle } from "@/types/libraryType";

// Backend API response format
interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * Library API response with metadata
 */
export interface LibraryApiResponse {
  data: LibraryArticle[];
  meta: PaginationMeta;
}

/**
 * Simple Library API Class
 */
export class LibraryApi {
  private readonly endpoint = "landing-library"; // keep internal proxy route if needed

  /**
   * Get library articles with pagination
   */
  async list(
    params?: QueryParams,
    options?: RequestOptions
  ): Promise<ApiClientResponse<LibraryApiResponse>> {
    try {
      const queryParams = new URLSearchParams();

      // Handle pagination
      if (params?.page) queryParams.set("page", params.page.toString());
      if (params?.limit) queryParams.set("limit", params.limit.toString());

      // Handle sorting
      if (params?.sort) queryParams.set("sort", params.sort);

      const queryString = queryParams.toString();
      const url = queryString
        ? `${this.endpoint}?${queryString}`
        : this.endpoint;

      const response = await api.get<LibraryApiResponse>(url, options); // This calls /api/landing-library which should proxy to real backend

      return response;
    } catch (error) {
      console.error("Error fetching library articles:", error);
      return {
        success: false,
        error: {
          status: 500,
          message:
            error instanceof Error
              ? error.message
              : "Failed to fetch library articles",
        },
      };
    }
  }

  /**
   * Get single library article by ID
   */
  async getById(
    id: string,
    options?: RequestOptions
  ): Promise<ApiClientResponse<LibraryArticle>> {
    try {
      const response = await api.get<LibraryArticle>(
        `${this.endpoint}/${id}`,
        options
      );
      return response;
    } catch (error) {
      console.error("Error fetching library article:", error);
      return {
        success: false,
        error: {
          status: 500,
          message:
            error instanceof Error
              ? error.message
              : "Failed to fetch library article",
        },
      };
    }
  }
}

// Export pre-configured instance
export const libraryApi = new LibraryApi();

// Export for convenience
export default libraryApi;
